import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import type { Place, VehicleType } from '@raahi/shared';
import { Shell } from '../layout/Shell.js';
import { MapView } from '../components/MapView.js';
import { BottomSheet } from '../components/BottomSheet.js';
import { VehicleTypePicker } from '../components/VehicleTypePicker.js';
import { CITY_DIRECTORY } from '../lib/places.js';
import { estimateFare, nearbyDrivers, requestRide } from '../api/trips.js';
import { ApiError } from '../api/client.js';
import { useActiveTrip } from '../state/activeTrip.js';
import { useToast } from '../components/Toast.js';

const DEFAULT_CITY = CITY_DIRECTORY[0]!;

function PlaceSelect({
  label,
  city,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  city: typeof CITY_DIRECTORY[number];
  value: Place | null;
  onChange: (place: Place) => void;
  placeholder: string;
}) {
  return (
    <label className="block text-xs font-semibold uppercase tracking-wide text-muted">
      {label}
      <select
        className="input mt-1 normal-case"
        value={value?.label ?? ''}
        onChange={(e) => {
          const next = city.places.find((p) => p.label === e.target.value);
          if (next) onChange(next);
        }}
      >
        <option value="" disabled>
          {placeholder}
        </option>
        {city.places.map((p) => (
          <option key={p.label} value={p.label}>
            {p.label}
          </option>
        ))}
      </select>
    </label>
  );
}

export function HomePage() {
  const navigate = useNavigate();
  const setTripId = useActiveTrip((s) => s.setTripId);
  const toast = useToast();

  const [cityIdx, setCityIdx] = useState(0);
  const city = CITY_DIRECTORY[cityIdx] ?? DEFAULT_CITY;
  const [pickup, setPickup] = useState<Place | null>(null);
  const [drop, setDrop] = useState<Place | null>(null);
  const [vehicleType, setVehicleType] = useState<VehicleType>('economy');
  const [paymentMethod, setPaymentMethod] = useState<'cash' | 'card'>('cash');
  const [error, setError] = useState<string | null>(null);

  const nearbyQuery = useQuery({
    queryKey: ['nearby', city.centre.lat, city.centre.lng, vehicleType],
    queryFn: () => nearbyDrivers({ lat: city.centre.lat, lng: city.centre.lng, type: vehicleType }),
    refetchInterval: 8000,
  });

  const estimates = useQuery({
    queryKey: ['estimates', pickup?.label, drop?.label],
    enabled: Boolean(pickup) && Boolean(drop),
    queryFn: async () => {
      if (!pickup || !drop) return null;
      const results = await Promise.all(
        (['bike', 'economy', 'premium'] as VehicleType[]).map(async (type) => {
          try {
            const e = await estimateFare({ type, pickup, drop });
            return [type, e.fare.total] as const;
          } catch {
            return [type, null] as const;
          }
        }),
      );
      return Object.fromEntries(results) as Record<VehicleType, number | null>;
    },
  });

  const request = useMutation({
    mutationFn: requestRide,
    onSuccess: (trip) => {
      setTripId(trip.id);
      toast.show('Looking for a driver near you.', 'info');
      navigate('/trip', { replace: true });
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : 'Could not request a ride. Please try again.';
      setError(msg);
      toast.show(msg, 'error');
    },
  });

  const fareMap = useMemo(
    () =>
      estimates.data
        ? {
            bike: estimates.data.bike ?? undefined,
            economy: estimates.data.economy ?? undefined,
            premium: estimates.data.premium ?? undefined,
          }
        : undefined,
    [estimates.data],
  );

  const canRequest = pickup && drop && Boolean(fareMap?.[vehicleType]) && !request.isPending;

  return (
    <Shell fullBleed>
      <div className="absolute inset-0">
        <MapView
          centre={pickup ?? city.centre}
          pickup={pickup ?? undefined}
          drop={drop ?? undefined}
          drivers={nearbyQuery.data?.map((d) => ({ id: d.id, lat: d.lat, lng: d.lng }))}
          follow={pickup && drop ? 'route' : 'centre'}
        />
      </div>

      <header className="pointer-events-none absolute inset-x-0 top-0 z-[1000] flex justify-center px-4 pt-[max(env(safe-area-inset-top),12px)]">
        <div className="pointer-events-auto flex w-full max-w-mobile items-center justify-between rounded-2xl bg-white/95 px-4 py-2 shadow-card backdrop-blur">
          <p className="font-display text-lg font-bold">Raahi</p>
          <select
            aria-label="City"
            className="num bg-transparent text-sm font-semibold focus:outline-none"
            value={cityIdx}
            onChange={(e) => {
              setCityIdx(Number(e.target.value));
              setPickup(null);
              setDrop(null);
            }}
          >
            {CITY_DIRECTORY.map((c, i) => (
              <option key={c.city} value={i}>
                {c.city}
              </option>
            ))}
          </select>
        </div>
      </header>

      <BottomSheet>
        <div className="space-y-3">
          <p className="font-display text-lg font-semibold">Where to?</p>
          <div className="grid grid-cols-2 gap-2">
            <PlaceSelect label="Pickup" city={city} value={pickup} onChange={setPickup} placeholder="Choose pickup" />
            <PlaceSelect label="Drop-off" city={city} value={drop} onChange={setDrop} placeholder="Choose drop" />
          </div>
          {!drop ? (
            <div className="-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-1">
              <span className="shrink-0 pr-1 pt-1 text-xs font-semibold uppercase tracking-wide text-muted">
                Popular
              </span>
              {city.places.slice(0, 4).map((p) => (
                <button
                  key={p.label}
                  type="button"
                  onClick={() => (pickup ? setDrop(p) : setPickup(p))}
                  className="shrink-0 rounded-full border border-line bg-white px-3 py-1 text-xs font-semibold text-ink hover:border-ink/50"
                >
                  {p.label}
                </button>
              ))}
            </div>
          ) : null}
          <VehicleTypePicker value={vehicleType} onChange={setVehicleType} fares={fareMap} />
          <div className="flex items-center justify-between rounded-2xl border border-line px-3 py-2">
            <span className="text-sm text-muted">Pay with</span>
            <div className="flex gap-1">
              {(['cash', 'card'] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setPaymentMethod(m)}
                  aria-pressed={paymentMethod === m}
                  className={
                    'rounded-full px-3 py-1 text-xs font-semibold capitalize ' +
                    (paymentMethod === m ? 'bg-ink text-white' : 'bg-line text-ink')
                  }
                >
                  {m}
                </button>
              ))}
            </div>
          </div>
          {error ? (
            <p role="alert" className="text-sm text-red-600">
              {error}
            </p>
          ) : null}
          <button
            type="button"
            className="btn-primary w-full"
            disabled={!canRequest}
            onClick={() => {
              if (!pickup || !drop) return;
              setError(null);
              request.mutate({ vehicleType, pickup, drop, paymentMethod });
            }}
          >
            {request.isPending ? 'Requesting…' : pickup && drop ? 'Request ride' : 'Set pickup and drop-off'}
          </button>
        </div>
      </BottomSheet>
    </Shell>
  );
}
